import React, { type FC, useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { Script } from "gatsby";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import { StaticImage } from "gatsby-plugin-image";
import { Link, useI18next } from "gatsby-plugin-react-i18next";

import BetaMode from "@components/beta_mode";
import ChatWidget from "@components/chat/chat_widget";
import CookieConsentBanner from "@components/cookie_consent_banner";
import FillInformationPopup from "@components/fill_information_popup";
import Footer from "@components/footer";
import Header from "@components/header";
import PromoAutoPopup from "@components/promo_auto_popup";
import RestrictedToPurchasePopup from "@components/restricted_to_purchase_popup";
import CookieType from "@constants/CookieType";
import { useAffiliate } from "@hooks/affiliate";
import {
  useAccountDetailState,
  useAccountDetailStore,
} from "@stores/account_detail";
import {
  useAuthenticationState,
  useAuthenticationStore,
} from "@stores/authentication";
import { useBootState } from "@stores/boot";
import {
  useConfigurationState,
  useConfigurationStore,
} from "@stores/configuration";
import { useCountryState } from "@stores/country";
import {
  rememberGateReturnTo,
  stripLanguagePrefix,
  usePasswordGateStore,
} from "@stores/password_gate";
import { prefetchSimCardPricing } from "@stores/sim-card";
import { getAffiliateLogoUrl } from "@utilities/affiliate";
import axios from "@utilities/axios";
import { loadCurrencies } from "@utilities/currencies";
import {
  clearUserData,
  isBetaMode,
  isDebugMode,
  isProductionMode,
} from "@utilities/helper";
import {
  detectLandingPage,
  rememberLandingPage,
  trackLandingPageVisit,
} from "@utilities/landing-page-tracking";
import {
  isCurrencyChosenByVisitor,
  isRussianCardEnabled,
  isRussianVisitor,
  russianCardCurrency,
} from "@utilities/paymentConfiguration";
import Cookies from "js-cookie";

const Layout: FC<{
  noLayout?: boolean;
  showHeader?: boolean;
  children?: any;
}> = ({ noLayout, showHeader, children }) => {
  const { path, language, languages, changeLanguage, navigate } = useI18next();
  const { affiliate, isResolvingAffiliate } = useAffiliate();
  const affiliateLogoUrl = affiliate ? getAffiliateLogoUrl(affiliate) : "";

  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  const authenticationState = useAuthenticationState();
  const accountDetailState = useAccountDetailState();
  const configurationState = useConfigurationState();
  const bootState = useBootState();
  const countryState = useCountryState();

  const authPaths = [
    "login",
    "register",
    "forgot-password",
    "oauth",
    "complete-account",
    // The email-verification link target must not be sent to /complete-account
    // by the forced-password gate before it has verified the address.
    "email-verification",
  ];
  const noLayoutPaths = [
    "promo/airport-free-esim",
    "openai/esims",
    "best-esim-for-japan",
    "best-esim-for-china",
    "best-esim-for-usa",
    "best-esim-for-egypt",
    "best-esim-for-europe",
    "best-esim-for-pakistan",
    "best-esim-for-germany",
    "best-esim-for-turkiye",
    "best-esim-for-albania",
    "best-esim-for-thailand",
    "best-esim-for-indonesia",
    "best-esim-for-global",
    "best-esim-for-greece",
    "best-esim-for-morocco",
    "best-esim-for-united-kingdom",
    "best-esim-for-south-korea",
    "bnesim-in-chatgpt",
    "affiliate-rewards-terms-and-conditions",
    // ArcoPay's fail page loads inside the checkout's payment widget.
    "arcopay-fail",
    // Standalone card opened from the verification email.
    "email-verification",
  ];
  const hideFillInformationPaths = ["thank-you", "promo/airport-free-esim/"];
  // Transactional pages opened from an email: no marketing or profile popups
  // on top of the result card.
  const noPopupPaths = ["email-verification"];

  const isNoLayoutPage =
    noLayout === true ||
    noLayoutPaths.some(noLayoutPath => path.includes(noLayoutPath));
  const isHideFillInformationPage = hideFillInformationPaths.some(
    hideFillInformationPath => path.includes(hideFillInformationPath),
  );
  const isNoPopupPage = noPopupPaths.some(noPopupPath =>
    path.includes(noPopupPath),
  );
  const isAuthPage = authPaths.some(authPath => path.includes(authPath));
  const isGeneralLayout = showHeader ?? !isAuthPage;

  const initializeData = async () => {
    // The auth-bootstrap phase is wrapped so `isAuthBootstrapped` is flipped
    // even if something in here throws. Consumers gated on it (e.g. the BNE
    // Guard `?product=` deep link) would otherwise hang forever on a failure.
    // `finally` without `catch` keeps the original control flow: the error
    // still propagates and the rest of initializeData is still skipped.
    try {
      await loadCurrencies(language);

      // App → Web single sign-on: if the mobile app opened this page with a
      // one-shot `?sid=`, redeem it for the web's own session token before the
      // cookie-token path below, so a fresh app handoff always wins. `sid` is
      // stripped from the URL *before* the redeem (single-use; keep it out of
      // history/referrer): an expired or already-used code answers 903, and
      // anything that reloads the page must not bring the same code back.
      if (typeof window !== "undefined" && window.location.search) {
        const bootParams = new URLSearchParams(window.location.search);
        const ssoSid = bootParams.get("sid");
        if (ssoSid) {
          bootParams.delete("sid");
          const cleanedSearch = bootParams.toString();
          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}${window.location.hash}`,
          );
          await new Promise<void>(resolve => {
            void authenticationState.loginWithSSO(
              ssoSid,
              () => {
                resolve();
              },
              () => {
                void accountDetailState.load();
              },
            );
          });
        }
      }

      const tokenFromCookie = Cookies.get(CookieType.TOKEN) ?? "";
      if (isProductionMode()) {
        if (authenticationState.getToken() !== tokenFromCookie) {
          if (tokenFromCookie) {
            authenticationState.setToken(tokenFromCookie);
            void accountDetailState.load();
          } else {
            clearUserData();
          }
          if (isDebugMode()) {
            console.log("loaded token from cookie 🍪.");
          }
        } else if (authenticationState.isAuthenticated()) {
          void accountDetailState.load();
        }
      }
    } finally {
      bootState.setAuthBootstrapped(true);
    }

    const currencyFromCookie = Cookies.get(CookieType.CURRENCY) ?? "";
    if (isProductionMode()) {
      if (configurationState.currency !== currencyFromCookie) {
        configurationState.updateCurrency(
          currencyFromCookie ?? process.env.GATSBY_DEFAULT_CURRENCY,
        );
        if (isDebugMode()) {
          console.log("loaded currency token from cookie 🍪.");
        }
      }
    }

    let isCouponCodeUpdated = false;
    if (typeof window !== "undefined" && window.location.search) {
      const params = new URLSearchParams(window.location.search);

      // The `?a=CODE` affiliate-param validation is owned by
      // `AffiliateProvider` (src/hooks/affiliate.tsx) so it can surface a
      // pending-skeleton state to header/footer/auth consumers.

      const paramCouponCode = params.get("coupon-code") ?? "";
      if (paramCouponCode) {
        configurationState.updateCouponCode(paramCouponCode);
        isCouponCodeUpdated = true;
        if (isDebugMode()) {
          console.log("registered coupon code.");
        }
      }

      const currencyParam = params.get("currency");
      if (currencyParam) {
        configurationState.updateCurrency(currencyParam.toUpperCase());
        if (isDebugMode()) {
          console.log("updated currency.");
        }
      }

      const langFromCookie = Cookies.get(CookieType.LANG) ?? "";
      let isLanguageChanged: boolean = false;
      if (!params.get("product")) {
        if (authenticationState.isAuthenticated()) {
          const formData = new FormData();
          formData.append("action", "local-info-only");
          const response = await axios.post("/my_account_details/", formData);
          const savedLanguage = response.data?.language ?? "";
          if (!!savedLanguage && savedLanguage !== "en") {
            void changeLanguage(langFromCookie);
            isLanguageChanged = true;
          }
        }
        if (!isLanguageChanged) {
          if (isProductionMode()) {
            if (language !== langFromCookie) {
              if (langFromCookie) {
                void changeLanguage(langFromCookie);
                if (isDebugMode()) {
                  console.log("loaded language token from cookie 🍪.");
                }
              }
            }
          }
        }
      }
    }

    if (!isCouponCodeUpdated) {
      const formData = new FormData();
      formData.append("action", "get-default-coupon");
      formData.append("a", configurationState.affiliate.toUpperCase());
      void axios
        .post("/country_rates_ext/", formData)
        .then(response => {
          if (response.data?.coupon) {
            configurationState.updateCouponCode(response.data.coupon?.code);
            if (isDebugMode()) {
              console.log("registered default coupon code.");
            }
          }
        })
        .catch((error: Error) => {
          console.error("[layout] default coupon:", error.message);
        });
    }

    void axios
      .get("/esim_app_status/?action=loc")
      .then(async response => {
        if (isDebugMode()) {
          console.log("JSON response:", response);
        }
        if (response?.data.loc?.iso2) {
          configurationState.setCountryCodeFromApi(response.data.loc.iso2);
        }
      })
      .catch((error: Error) => {
        console.error("[layout] location:", error.message);
      });

    prefetchSimCardPricing();
    if (authenticationState.isAuthenticated()) {
      void countryState.load();
    }
  };

  useEffect(() => {
    if (isHydrated) {
      void initializeData();
    } else {
      void useAccountDetailStore.persist.rehydrate();
      void useAuthenticationStore.persist.rehydrate();
      void useConfigurationStore.persist.rehydrate();
      setIsHydrated(true);
    }
  }, [isHydrated]);

  // Visitors from Russia get RUB, the currency they pay in by Russian card
  // (CloudPayments), on every page load -- until they pick a currency themselves
  // in the currency selector, which then sticks for the session. Never over an
  // explicit `?currency=`. Only while that rail is offered (beta, see the plans
  // checkout): Stripe cannot charge RUB. Not keyed on `currency`, so a
  // programmatic move off RUB (the checkout falling back to Stripe) is not
  // undone mid-page.
  useEffect(() => {
    const { countryCodeFromApi, currencies, currency } = configurationState;
    if (
      !isHydrated ||
      !isRussianCardEnabled ||
      !isBetaMode() ||
      !isRussianVisitor(countryCodeFromApi) ||
      isCurrencyChosenByVisitor() ||
      new URLSearchParams(window.location.search).has("currency") ||
      !currencies.some(item => item.currency === russianCardCurrency)
    ) {
      return;
    }
    if (currency !== russianCardCurrency) {
      configurationState.updateCurrency(russianCardCurrency);
    }
  }, [
    isHydrated,
    configurationState.countryCodeFromApi,
    configurationState.currencies.length,
  ]);

  // best-esim-for-* pages: count the visit and remember the page for purchase
  // attribution (ai_docs/specs/destination-landing-pages.md §7.1). Keyed on
  // `path` so client-side navigation between destination pages is picked up.
  useEffect(() => {
    const landingPage = detectLandingPage(window.location.pathname);
    if (landingPage === null) {
      return;
    }
    rememberLandingPage(landingPage);
    trackLandingPageVisit(landingPage);
  }, [path]);

  // Forced-password gate: a session whose backend `is_password_changed` flag
  // is 0 (registration abandoned before the password step, so the account
  // still runs on an auto-generated password) may keep its session but must
  // finish setup before using the site or crossing to app.bnesim.com.
  // Auth pages are exempt (the register/login flows drive their own
  // name/password steps there), and an open login/register modal suppresses
  // the gate for the same reason. Anything else redirects to
  // /complete-account, which re-verifies the flag server-side on mount.
  const isPasswordGateSuppressed = usePasswordGateStore(
    state => state.suppressCount > 0,
  );
  useEffect(() => {
    if (!isHydrated) return;
    if (
      authenticationState.isAuthenticated() &&
      authenticationState.is_password_changed === 0 &&
      !isPasswordGateSuppressed &&
      !isAuthPage
    ) {
      // Keep the interrupted URL (path + query) so /complete-account sends
      // the visitor back there instead of to the home page: a deep link's
      // product, coupon and campaign parameters must survive the detour.
      rememberGateReturnTo(
        stripLanguagePrefix(window.location.pathname, languages),
        window.location.search,
      );
      void navigate("/complete-account");
    }
  }, [
    isHydrated,
    path,
    authenticationState.is_password_changed,
    authenticationState.token,
    isPasswordGateSuppressed,
  ]);

  return (
    <div className="contents selection:bg-primary/80 selection:text-white">
      <Script src="/js/trackers.js" />
      <Script src="https://aladinia.app/tracking.js?key=yb_fb1d8679" />
      {!isNoLayoutPage ? (
        <>
          {isGeneralLayout ? (
            <>
              <Header />
              <div className="pt-24">
                <main>{children}</main>
              </div>
              <Footer />
            </>
          ) : (
            <div className="container flex min-h-dvh flex-col items-center justify-center gap-3 py-4 sm:gap-4 md:gap-6 md:py-5 lg:flex-row lg:justify-between lg:gap-0 lg:px-6">
              <div className="flex w-full items-center justify-center lg:w-auto lg:max-w-[320px]">
                {isResolvingAffiliate ? (
                  <div
                    className="aspect-[300/69] w-[60vw] animate-pulse rounded-md bg-gray-200 lg:w-full"
                    aria-hidden
                  />
                ) : affiliate !== null ? (
                  <OutboundLink
                    href={affiliate.website || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none"
                  >
                    <img
                      className="size-full w-[60vw] lg:w-full"
                      src={affiliateLogoUrl || "/images/bnesim_logo.svg"}
                      loading="eager"
                      alt={`${affiliate.name} logo`}
                    />
                  </OutboundLink>
                ) : (
                  <Link
                    to="/"
                    className="transition duration-300 hover:opacity-80 focus:opacity-80 focus:outline-none"
                  >
                    <StaticImage
                      className="size-full w-[60vw] lg:w-full"
                      src="../assets/images/bnesim_logo.svg"
                      loading="eager"
                      alt="Official BNESIM Logo – A sleek modern design representing the world’s best travel eSIM provider for global connectivity and seamless communication."
                      placeholder="none"
                    />
                  </Link>
                )}
              </div>
              <main className="w-full lg:max-w-[calc(100%-344px)]">
                {children}
              </main>
            </div>
          )}
        </>
      ) : (
        <main className="w-full">{children}</main>
      )}
      <ToastContainer className="text-sm" draggable />
      <ChatWidget />
      {!isHideFillInformationPage && !isNoPopupPage && <FillInformationPopup />}
      {!isNoPopupPage && <RestrictedToPurchasePopup />}
      {!isNoPopupPage && <PromoAutoPopup />}
      <CookieConsentBanner />
      <BetaMode />
    </div>
  );
};

export default Layout;
